import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { CohortSpendMember, CohortSpendReport } from '@/types';
import { count, day, inr, membersCsv, pct, ruleText, staleWarning } from './cohortSpendMath';

const { Text } = Typography;

const PAGE_SIZE = 50;
/** The backend's largest page; the CSV holds the top members by spend up to this. */
const EXPORT_LIMIT = 1000;

interface Props {
  cohort: { id: string; name: string } | null;
  onClose: () => void;
}

const memberColumns: ColumnsType<CohortSpendMember> = [
  {
    title: 'Player',
    key: 'player',
    render: (_, m) => (
      <Space direction="vertical" size={0}>
        <Link to={`/players/${m.userId}`}>{m.displayName || 'No name'}</Link>
        <Text type="secondary" style={{ fontSize: 11 }} copyable={{ text: m.userId }}>
          {m.userId.slice(0, 8)}
        </Text>
      </Space>
    ),
  },
  { title: 'Spend', dataIndex: 'inr', align: 'right', render: (v: number) => inr(v) },
  { title: 'Purchases', dataIndex: 'purchases', align: 'right', render: (v: number) => count(v) },
  { title: 'First buy', dataIndex: 'firstPurchaseAt', render: (v: string | null) => day(v) },
  { title: 'Last buy', dataIndex: 'lastPurchaseAt', render: (v: string | null) => day(v) },
  { title: 'Last seen', dataIndex: 'lastSeenAt', render: (v: string | null) => day(v) },
  {
    title: (
      <Tooltip title="Does this player's real INR spend fit the cohort's spend rule today?">
        Fits rule
      </Tooltip>
    ),
    dataIndex: 'spendFitsRule',
    render: (v: boolean | null) =>
      v === null ? '—' : v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
  },
  { title: 'Platform', dataIndex: 'platform', render: (v: string | null) => v ?? '—' },
  { title: 'In cohort since', dataIndex: 'assignedAt', render: (v: string) => day(v) },
];

interface BodyProps {
  report: CohortSpendReport;
  page: number;
  loading: boolean;
  onPage: (page: number) => void;
}

/** The drawer content for a loaded report (separate so it renders without fetching). */
export const CohortSpendBody: React.FC<BodyProps> = ({ report, page, loading, onPage }) => {
  const s = report.summary;
  const warning = staleWarning(report);
  const rule = ruleText(report.spendingRange);
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Spend = completed, real (not tester or sandbox) INR purchases, all time, as the cohort rules
        count it. Coin and token purchases are not money. Updated every 2 minutes.
      </Text>
      {warning && <Alert type="warning" showIcon message="Stale members" description={warning} />}

      <Row gutter={[12, 12]}>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Members" value={count(s.members)} />
          </Card>
        </Col>
        {s.spendFitsRule !== null && (
          <Col span={4}>
            <Card size="small">
              <Statistic
                title={<Tooltip title={`Spend fits ${rule} today`}>Fit the rule today</Tooltip>}
                value={count(s.spendFitsRule)}
                suffix={<Text type="secondary">{pct(s.spendFitsRule, s.members)}</Text>}
              />
            </Card>
          </Col>
        )}
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Payers"
              value={count(s.payers)}
              suffix={<Text type="secondary">{pct(s.payers, s.members)}</Text>}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Total spend" value={inr(s.totalInr)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title={
                <Tooltip title="Median lifetime spend of the paying members">
                  Median (payers)
                </Tooltip>
              }
              value={inr(s.medianInr)}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Top spender" value={inr(s.maxInr)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Purchases" value={count(s.purchases)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Bought in last 7 days" value={count(s.boughtLast7d)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title="Seen in last 7 days" value={count(s.seenLast7d)} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title={
                <Tooltip title="The daily recalculation only re-checks players seen in the last 35 days">
                  Not seen 35+ days
                </Tooltip>
              }
              value={count(s.notSeen35d)}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={10}>
          <Card size="small" title="Lifetime spend bands">
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={report.buckets}
              columns={[
                { title: 'Spend', dataIndex: 'label' },
                {
                  title: 'Members',
                  dataIndex: 'members',
                  align: 'right',
                  render: (v: number) => `${count(v)} (${pct(v, s.members)})`,
                },
                {
                  title: 'Spend',
                  dataIndex: 'inr',
                  align: 'right',
                  render: (v: number) => inr(v),
                },
              ]}
            />
          </Card>
        </Col>
        <Col span={14}>
          <Card size="small" title="What they buy (top 20 by spend)">
            {report.products.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No INR purchases" />
            ) : (
              <Table
                size="small"
                rowKey="productId"
                pagination={false}
                scroll={{ y: 300 }}
                dataSource={report.products}
                columns={[
                  { title: 'Product', dataIndex: 'productId' },
                  {
                    title: 'Buys',
                    dataIndex: 'purchases',
                    align: 'right',
                    render: (v: number) => count(v),
                  },
                  {
                    title: 'Buyers',
                    dataIndex: 'buyers',
                    align: 'right',
                    render: (v: number) => count(v),
                  },
                  {
                    title: 'Spend',
                    dataIndex: 'inr',
                    align: 'right',
                    render: (v: number) => inr(v),
                  },
                  {
                    title: 'Share',
                    dataIndex: 'inr',
                    key: 'share',
                    align: 'right',
                    render: (v: number) => pct(v, s.totalInr),
                  },
                  {
                    title: 'Avg price',
                    dataIndex: 'avgPriceInr',
                    align: 'right',
                    render: (v: number) => inr(v),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Members, highest spend first">
        <Table<CohortSpendMember>
          size="small"
          rowKey="userId"
          loading={loading}
          dataSource={report.members}
          columns={memberColumns}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: s.members,
            showSizeChanger: false,
            showTotal: (t) => `${count(t)} members`,
            onChange: onPage,
          }}
        />
      </Card>
    </Space>
  );
};

/**
 * Click a cohort's member count: who is in it and what they have spent. Money = completed, real
 * INR purchases, the way the cohort rules count it. Reads GET /admin/cohorts/:id/spend.
 */
export const CohortSpendDrawer: React.FC<Props> = ({ cohort, onClose }) => {
  const [report, setReport] = useState<CohortSpendReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(
    async (p: number) => {
      if (!cohort) return;
      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.getCohortSpend(cohort.id, {
          limit: PAGE_SIZE,
          offset: (p - 1) * PAGE_SIZE,
        });
        setReport(res.data);
      } catch (e) {
        console.error('Failed to load cohort spend', e);
        setError('Could not load the spend details. Large cohorts can take ~10 s; try again.');
      } finally {
        setLoading(false);
      }
    },
    [cohort]
  );

  useEffect(() => {
    setReport(null);
    setPage(1);
    if (cohort) void load(1);
  }, [cohort, load]);

  const exportCsv = async () => {
    if (!cohort) return;
    setExporting(true);
    try {
      const res = await adminApi.getCohortSpend(cohort.id, { limit: EXPORT_LIMIT, offset: 0 });
      const blob = new Blob([membersCsv(res.data.members)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cohort-${res.data.slug}-members-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export cohort members', e);
      message.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rule = report ? ruleText(report.spendingRange) : null;

  return (
    <Drawer
      open={cohort !== null}
      onClose={onClose}
      width={1100}
      title={
        <Space>
          {cohort?.name} — members and spend
          {rule && <Tag>Spend rule {rule}</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load(page)} disabled={loading}>
            Refresh
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => void exportCsv()}
            loading={exporting}
            disabled={!report || report.summary.members === 0}
          >
            {report && report.summary.members > EXPORT_LIMIT
              ? `CSV (top ${count(EXPORT_LIMIT)} by spend)`
              : 'CSV'}
          </Button>
        </Space>
      }
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {!report && loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )}
      {report && (
        <CohortSpendBody
          report={report}
          page={page}
          loading={loading}
          onPage={(p) => {
            setPage(p);
            void load(p);
          }}
        />
      )}
    </Drawer>
  );
};
